'use strict';

// The user's 1Sat Ordinals holdings, organized for the Web3Keys wallet: grouped by the SmartLedger
// app that minted each token (via MAP `app`), with the actual media (image/video/audio) surfaced.

const api = require('../api');

async function fetchCollection(address) {
  if (!address) return [];
  try { const r = await api.collection(address); return (r && r.items) || []; }
  catch (_) { return []; }
}

function mediaKind(ct) {
  const t = (ct || '').toLowerCase();
  if (t.startsWith('image/')) return 'image';
  if (t.startsWith('video/')) return 'video';
  if (t.startsWith('audio/')) return 'audio';
  if (t.startsWith('text/') || t.includes('json') || t.includes('markdown')) return 'text';
  return 'other';
}

// Friendly labels/icons for known SmartLedger apps (matched on the MAP `app` value).
const APPS = {
  raregeneration: { label: 'Rare Generation', icon: '🎵' },
  ticketmint: { label: 'TicketMint', icon: '🎟' },
  songdrop: { label: 'SongDrop', icon: '🎧' },
  verifiedgrades: { label: 'Verified Grades', icon: '🎓' },
  smartlms: { label: 'SmartLMS', icon: '📚' },
  collectible: { label: 'Collectibles', icon: '🃏' },
  collectibles: { label: 'Collectibles', icon: '🃏' },
  digitaltwin: { label: 'Digital Twins', icon: '🃏' },
  web3keys: { label: 'Web3Keys', icon: '🔑' },
};
const MEDIA_BUCKETS = {
  image: { label: 'Art & Images', icon: '🖼' },
  audio: { label: 'Music & Audio', icon: '🎵' },
  video: { label: 'Video', icon: '🎬' },
  text: { label: 'Documents', icon: '📄' },
  other: { label: 'Collectibles', icon: '◆' },
};

const prettify = (s) => String(s).replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim().replace(/\b\w/g, (c) => c.toUpperCase());
function appInfo(app) {
  const k = String(app).toLowerCase().replace(/[\s_-]+/g, '');
  if (APPS[k]) return { key: 'app:' + k, label: APPS[k].label, icon: APPS[k].icon };
  return { key: 'app:' + k, label: prettify(app), icon: '📦' }; // unknown app — still its own group
}

// Group items by minting app; tokens with no app fall back to media-type buckets. App groups first.
function groupByApp(items) {
  const groups = new Map();
  for (const it of items) {
    let g;
    if (it.app) g = appInfo(it.app);
    else { const mk = mediaKind(it.contentType); const b = MEDIA_BUCKETS[mk] || MEDIA_BUCKETS.other; g = { key: 'media:' + mk, label: b.label, icon: b.icon, fallback: true }; }
    if (!groups.has(g.key)) groups.set(g.key, { key: g.key, label: g.label, icon: g.icon, fallback: !!g.fallback, items: [] });
    groups.get(g.key).items.push(it);
  }
  return [...groups.values()].sort((a, b) => (a.fallback ? 1 : 0) - (b.fallback ? 1 : 0) || b.items.length - a.items.length);
}

// Format a redeemable value + currency (most tokens carry platform value, not BSV).
function fmtValue(v, cur) {
  if (v == null || v === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  const c = (cur || 'USD').toUpperCase();
  return c === 'USD' ? '$' + n.toFixed(2) : n.toFixed(2) + ' ' + c;
}
// The action to take on a token, by its kind: use a ticket, redeem a credit/royalty, or open.
function actionLabel(kind) {
  const k = (kind || '').toLowerCase();
  if (/ticket|pass|entry|admission/.test(k)) return 'Use ticket';
  if (/royalt|credit|payout|redeem|balance|cash/.test(k)) return 'Redeem';
  return 'Open';
}
const isSpent = (status) => /redeem|spent|used|inactive|expired|void/.test(String(status || '').toLowerCase());

module.exports = { fetchCollection, mediaKind, groupByApp, appInfo, fmtValue, actionLabel, isSpent };
